import { useState } from "react";
import { AlertTriangle, Ban, Check, FileText, Loader2, Pencil, Quote, Sparkles } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { resolveRecovery } from "./api";
import { RecoveryCandidate, RecoveryVerdict, SectionRecovery } from "./types";

interface GapEvidenceProps {
  standardId: string;
  sectionId: string;
  recovery: SectionRecovery;
  obligationSignals: number;
  busy: boolean;
  /** Moves the document pane to the sentence and marks it. */
  onFocus: (candidate: RecoveryCandidate) => void;
  onChanged: () => Promise<void>;
}

const VERDICT: Record<
  RecoveryVerdict,
  { status: "pass" | "warning" | "fail" | "info"; label: string }
> = {
  "likely-requirement": { status: "warning", label: "Likely requirement" },
  "likely-preamble": { status: "info", label: "Likely preamble" },
  "likely-definition": { status: "info", label: "Likely definition" },
  "likely-cross-reference": { status: "info", label: "Likely cross-reference" },
  ambiguous: { status: "warning", label: "Ambiguous" },
};

/**
 * A possible extraction gap, presented as a decision rather than an investigation.
 *
 * The rule this component exists to enforce: never merely tell a reviewer that a problem exists when
 * Verity can identify the evidence that caused it. So every sentence that triggered the gap is here,
 * verbatim; each carries Verity's verdict and its reasoning; and where the verdict is "this is a
 * requirement the first pass missed", the proposed rule is written out ready to accept.
 *
 * The reviewer's job is to agree, correct, or disagree — never to go and find the sentence themselves
 * and compose the requirement by hand. That was the old panel, and it made the human the extractor.
 */
const GapEvidence = ({
  standardId,
  sectionId,
  recovery,
  obligationSignals,
  busy,
  onFocus,
  onChanged,
}: GapEvidenceProps) => {
  const [working, setWorking] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ subject: string; requirement: string }>({
    subject: "",
    requirement: "",
  });
  const [error, setError] = useState<string | null>(null);

  const act = async (candidateId: string, action: "accept" | "reject", edits?: typeof draft) => {
    setWorking(candidateId);
    setError(null);
    try {
      await resolveRecovery(standardId, sectionId, candidateId, action, edits);
      setEditing(null);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That could not be saved.");
    } finally {
      setWorking(null);
    }
  };

  const outstanding = recovery.candidates.filter((c) => !c.resolution);
  const settled = recovery.candidates.filter((c) => c.resolution);

  // Recovery could not run. Say so plainly — an unanalysed gap must not masquerade as an analysed one.
  if (recovery.failure) {
    return (
      <div className="vr-no-rules vr-failed">
        <p className="vr-no-rules-title">The second pass could not run</p>
        <p>
          {obligationSignals} sentence{obligationSignals === 1 ? "" : "s"} in this section use
          obligation language and nothing was extracted, but the analysis that would tell you which of
          them matter failed to complete. Re-processing this chapter will try again.
        </p>
        <p className="vr-cell-code">{recovery.failure}</p>
      </div>
    );
  }

  return (
    <div className="vr-gap-evidence">
      <div className="vr-gap-lead">
        <AlertTriangle size={14} />
        <div>
          <strong>
            {outstanding.length} sentence{outstanding.length === 1 ? "" : "s"} to decide on
          </strong>
          <p>
            Nothing was extracted from this section, so Verity went back over every sentence that
            reads like an obligation and judged each one. Its answers are below — accept, correct or
            disagree.
          </p>
        </div>
      </div>

      {error && <div className="vr-form-error">{error}</div>}

      {[...outstanding, ...settled].map((candidate) => {
        const verdict = VERDICT[candidate.verdict];
        const isEditing = editing === candidate.id;
        const spinning = working === candidate.id;
        const resolved = candidate.resolution;

        return (
          <div
            key={candidate.id}
            className={`vr-gap-card ${resolved ? "vr-resolved" : ""}`}
            onClick={() => onFocus(candidate)}
          >
            <div className="vr-gap-card-head">
              <StatusBadge status={verdict.status} label={verdict.label} />
              <span className="vr-cell-dim">
                {Math.round(candidate.confidence * 100)}% confident · printed page{" "}
                {candidate.printedPage ?? "—"} · pdf {candidate.pdfPage}
              </span>
              {resolved && (
                <span className={resolved.action === "accepted" ? "vr-ok" : "vr-cell-dim"}>
                  {resolved.action === "accepted" ? "Accepted" : "Not a requirement"}
                  {resolved.automatic && " automatically"}
                </span>
              )}
            </div>

            {/* The evidence, first and verbatim. This is the sentence the reviewer would otherwise
                have had to go and find for themselves. */}
            <blockquote className="vr-rule-quote">
              <Quote size={10} />
              <span>“{candidate.quote}”</span>
            </blockquote>

            <p className="vr-gap-assessment">
              <Sparkles size={11} /> {candidate.assessment}
            </p>

            {candidate.proposed && (
              <div className="vr-gap-proposal">
                <div className="vr-gap-proposal-label">
                  <FileText size={11} /> Suggested requirement
                </div>
                {isEditing ? (
                  <div className="vr-rule-edit" onClick={(e) => e.stopPropagation()}>
                    <div className="vr-field">
                      <label>Subject</label>
                      <input
                        className="vr-input"
                        value={draft.subject}
                        onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                      />
                    </div>
                    <div className="vr-field">
                      <label>Requirement</label>
                      <input
                        className="vr-input"
                        value={draft.requirement}
                        onChange={(e) => setDraft({ ...draft, requirement: e.target.value })}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="vr-rule-text">
                    <strong>{candidate.proposed.subject}</strong> — {candidate.proposed.requirement}
                  </p>
                )}
                <div className="vr-gap-proposal-meta">
                  {candidate.proposed.ruleType} · {candidate.proposed.checkStrategy}
                  {candidate.proposed.externalRefs.length > 0 && (
                    <> · refers to {candidate.proposed.externalRefs.join("; ")}</>
                  )}
                </div>
              </div>
            )}

            {!resolved && (
              <div className="vr-rule-actions" onClick={(e) => e.stopPropagation()}>
                {candidate.proposed && (
                  <>
                    <button
                      className="vr-rowbtn"
                      disabled={busy || spinning}
                      onClick={() =>
                        void act(candidate.id, "accept", isEditing ? draft : undefined)
                      }
                    >
                      {spinning ? (
                        <Loader2 size={12} className="vr-spin-inline" />
                      ) : (
                        <Check size={12} />
                      )}{" "}
                      {isEditing ? "Save and accept" : "Accept requirement"}
                    </button>
                    <button
                      className="vr-rowbtn"
                      disabled={busy || spinning}
                      onClick={() => {
                        setEditing(isEditing ? null : candidate.id);
                        setDraft({
                          subject: candidate.proposed!.subject,
                          requirement: candidate.proposed!.requirement,
                        });
                      }}
                    >
                      <Pencil size={12} /> {isEditing ? "Cancel edit" : "Edit"}
                    </button>
                  </>
                )}
                <button
                  className="vr-rowbtn"
                  disabled={busy || spinning}
                  onClick={() => void act(candidate.id, "reject")}
                  title="Record that this sentence creates no independently actionable obligation"
                >
                  <Ban size={12} />{" "}
                  {candidate.proposed ? "Not a requirement" : "Agree — not a requirement"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GapEvidence;
